import {
  type InsuranceBuilder,
  getActionablePlanFamilyCarrierTypes,
} from '@/types/insurance-builder.type.ts';
import {
  CARRIER_TYPE_ORDER,
  type CarrierType,
  SERVICED_CARRIER_TYPES,
  type UnverifiedInsurance,
  type VerifiedInsurance,
  carrierTypesShareFamily,
  hasCarrierType,
} from '@/types/insurance.type.ts';

export function setCarrierTypes(
  builder: InsuranceBuilder,
  carrierTypes: Set<CarrierType>
): InsuranceBuilder {
  return {
    ...builder,
    carrierTypes: Array.from(carrierTypes),
  };
}

export function getCarrierTypesNeedingInput(builder: InsuranceBuilder): CarrierType[] {
  const carrierTypesAlreadyInput = [
    ...[
      ...builder.queuedInsurances,
      ...builder.confirmedInsurances,
      ...builder.verifiedInsurances,
    ].map((i) => i.carrier.carrierType),
    ...builder.unprovidedInsurances.map((i) => i.carrierType),
  ];
  return builder.carrierTypes
    .filter((type) =>
      carrierTypesAlreadyInput.every((inputType) => !carrierTypesShareFamily(inputType, type))
    )
    .sort((a, b) => CARRIER_TYPE_ORDER.indexOf(a) - CARRIER_TYPE_ORDER.indexOf(b));
}

export function allCarriersActionable(builder: InsuranceBuilder): boolean {
  const serviceableCarrierTypes = builder.carrierTypes.filter((t) => SERVICED_CARRIER_TYPES.has(t));

  if (serviceableCarrierTypes.length === 0) return false;

  const actionableTypes = getActionablePlanFamilyCarrierTypes(builder);
  return serviceableCarrierTypes.every((t) => actionableTypes.has(t));
}

export function removeInsurance(
  builder: InsuranceBuilder,
  insurance: UnverifiedInsurance | VerifiedInsurance
): InsuranceBuilder {
  return removeAllOfType(builder, insurance.carrier.carrierType);
}

function removeAllOfType(builder: InsuranceBuilder, carrierType: CarrierType): InsuranceBuilder {
  return {
    ...builder,
    unverifiedInsurances: builder.unverifiedInsurances.filter(
      (i) => !hasCarrierType(i, carrierType)
    ),
    unprovidedInsurances: builder.unprovidedInsurances.filter((i) => i.carrierType !== carrierType),
    queuedInsurances: builder.queuedInsurances.filter((i) => !hasCarrierType(i, carrierType)),
    confirmedInsurances: builder.confirmedInsurances.filter((i) => !hasCarrierType(i, carrierType)),
    verifiedInsurances: builder.verifiedInsurances.filter((i) => !hasCarrierType(i, carrierType)),
  };
}
